/**
 * Overview: how much moved, by whom (people vs automation vs Monday imports),
 * what is open right now, and what the cleanup closed.
 */
import React, { useMemo, useState } from 'react';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Summary, WindowParams, flowApi, Origin, Domain } from './flowApi';
import {
  C, DOMAINS, DOMAIN_COLOR, DOMAIN_LABEL, DOMAIN_MARKER, DOMAIN_NOTE, ORIGINS, ORIGIN_COLOR, ORIGIN_LABEL, ORIGIN_MARKER, ORIGIN_NOTE,
  axisLine, axisTick, gridProps, STATIC, tooltipProps, wordToken, compareWordOrder,
} from './chartTheme';
import { ChartCard, EmptyNote, Remote, fmtDay, fmtN, useRemote } from './flowUi';
import { LivingLegend, Marker, MetricTile, useChartMotion } from '../../../components/tide';
import type { LegendItem } from '../../../components/tide';

const OPEN_WORK: Array<{ key: keyof Summary['kpis']['openWork']; label: string; domain: Domain }> = [
  { key: 'PA', label: 'PA cases', domain: 'PA' },
  { key: 'UMED', label: 'UMED cases', domain: 'UMED' },
  { key: 'FINANCE', label: 'Finance handoffs', domain: 'FINANCE' },
  { key: 'RESUBMITTALS', label: 'Resubmittals', domain: 'MANIFEST' },
  { key: 'REPLACEMENTS', label: 'Replacements', domain: 'MANIFEST' },
  { key: 'MANIFEST_UNDECIDED', label: 'Manifest undecided', domain: 'MANIFEST' },
  { key: 'PROVIDER_VISITS', label: 'Provider visits', domain: 'PROVIDER' },
];
const OPEN_WORK_DOMAINS: Domain[] = ['PA', 'UMED', 'FINANCE', 'MANIFEST', 'PROVIDER'];

const pct = (n: number, total: number) => (total > 0 ? Math.round((n / total) * 100) : 0);

export default function OverviewView({ window: w }: { window: WindowParams }) {
  const { state, retry } = useRemote<Summary>(`summary|${w.from}|${w.to}`, () => flowApi.summary(w));
  return <Remote state={state} retry={retry} skeleton="overview">{(data) => <OverviewBody data={data} />}</Remote>;
}

function OverviewBody({ data }: { data: Summary }) {
  const { kpis, daily, cleanupDaily } = data;
  const origin = kpis.stepsByOrigin;
  const total = ORIGINS.reduce((s, o) => s + (origin[o] || 0), 0);
  const motion = useChartMotion();
  const [shownDomains, setShownDomains] = useState<string[]>(DOMAINS);
  const [shownWords, setShownWords] = useState<string[] | null>(null);

  const { cleanupRows, words, wordTotals } = useMemo(() => {
    const ws: string[] = [];
    const totals = new Map<string, number>();
    const byDate = new Map<string, Record<string, number | string>>();
    cleanupDaily.forEach((r) => {
      if (!ws.includes(r.word)) ws.push(r.word);
      totals.set(r.word, (totals.get(r.word) || 0) + r.count);
      const row = byDate.get(r.date) || { date: r.date };
      row[r.word] = ((row[r.word] as number) || 0) + r.count;
      byDate.set(r.date, row);
    });
    ws.sort(compareWordOrder); // known words in vocabulary order, then unknown ones: stable stack and legend order
    return { cleanupRows: Array.from(byDate.values()).sort((a, b) => String(a.date).localeCompare(String(b.date))), words: ws, wordTotals: totals };
  }, [cleanupDaily]);

  const domainTotals = useMemo(() => {
    const t: Record<Domain, number> = { PA: 0, MANIFEST: 0, UMED: 0, FINANCE: 0, COVERAGE: 0, PROVIDER: 0 };
    daily.forEach((d) => DOMAINS.forEach((k) => { t[k] += d[k] || 0; }));
    return t;
  }, [daily]);
  const plotted = DOMAINS.reduce((s, d) => s + domainTotals[d], 0);

  const openRows = OPEN_WORK.map((o) => ({ ...o, value: kpis.openWork[o.key] || 0, color: DOMAIN_COLOR[o.domain] }));
  const originRow = [{ name: 'Steps', PERSON: origin.PERSON || 0, SYSTEM: origin.SYSTEM || 0, IMPORT: origin.IMPORT || 0 }];
  const anyDaily = daily.some((d) => DOMAINS.some((k) => d[k] > 0));
  const visibleWords = shownWords ?? words;

  const domainItems: LegendItem[] = DOMAINS.map((d) => ({
    key: d, label: DOMAIN_LABEL[d], color: DOMAIN_COLOR[d], marker: DOMAIN_MARKER[d], description: DOMAIN_NOTE[d],
    value: domainTotals[d], unit: 'steps', share: { of: plotted, label: 'plotted steps' },
  }));
  const originItems: LegendItem[] = ORIGINS.map((o) => ({
    key: o, label: ORIGIN_LABEL[o], color: ORIGIN_COLOR[o], marker: ORIGIN_MARKER[o], description: ORIGIN_NOTE[o],
    value: origin[o] || 0, unit: 'steps', share: { of: total, label: 'all steps' },
  }));
  const openWorkItems: LegendItem[] = OPEN_WORK_DOMAINS.map((d) => ({
    key: d, label: DOMAIN_LABEL[d], color: DOMAIN_COLOR[d], marker: DOMAIN_MARKER[d],
    description: d === 'MANIFEST' ? 'Resubmittals, replacements and undecided manifest lines.' : DOMAIN_NOTE[d],
  }));
  const wordItems: LegendItem[] = words.map((wd) => {
    const t = wordToken(wd);
    return {
      key: wd, label: wd, color: t.color, marker: t.marker, glyph: t.glyph, value: wordTotals.get(wd) || 0, unit: 'closed',
      description: t.known ? undefined : 'Not in the PA vocabulary yet; shown as recorded.',
    };
  });

  return (
    <div className="space-y-5">
      {/* 8 tiles, no orphans: 1 column on phones; 2 on tablets (Steps and the long UMED tile span both);
          4 on desktop (Steps is 2x2, UMED spans 2), i.e. three full rows of four. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="kpi-grid">
        <MetricTile
          feature
          className="sm:col-span-2 lg:row-span-2"
          label="Steps"
          value={fmtN(kpis.steps)}
          caveat="Every recorded movement in the window, by who made it."
        >
          <span aria-hidden className="flex h-3 w-full overflow-hidden rounded-full" style={{ gap: 2, background: 'var(--tide-surface)' }} data-testid="steps-origin-bar">
            {ORIGINS.map((o) => (origin[o] ? <span key={o} style={{ width: `${pct(origin[o], total)}%`, background: ORIGIN_COLOR[o] }} /> : null))}
          </span>
          <ul className="mt-2 grid gap-1" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))' }}>
            {ORIGINS.map((o: Origin) => (
              <li key={o} className="flex items-center gap-1.5" style={{ color: 'var(--tide-ink)' }}>
                <Marker shape={ORIGIN_MARKER[o]} color={ORIGIN_COLOR[o]} size={10} />
                <span>{ORIGIN_LABEL[o]}</span>
                <strong className="tabular-nums">{fmtN(origin[o] || 0)}</strong>
              </li>
            ))}
          </ul>
        </MetricTile>
        <MetricTile label="Records moved" value={fmtN(kpis.recordsMoved)} caveat="distinct records" />
        <MetricTile label="Residents moved" value={fmtN(kpis.residentsMoved)} />
        <MetricTile label="Active people" value={fmtN(kpis.activePeople)} caveat="signed-in, acted" />
        <MetricTile
          label="Cleanup exits"
          value={fmtN(kpis.cleanupExits)}
          caveat={<>PA cases closed{kpis.cleanupExitsImported > 0 && <span className="block" title="Closed by the Monday sync, not counted above">+{fmtN(kpis.cleanupExitsImported)} imported</span>}</>}
        />
        <MetricTile label="Duplicates closed" value={fmtN(kpis.duplicatesClosed)} />
        <MetricTile label="Send-backs" value={fmtN(kpis.sendBacks)} caveat="cross-team returns" />
        <MetricTile className="sm:col-span-2" label="UMED cases reversed / resubmission" value={fmtN(kpis.reversals)} />
      </div>

      <ChartCard
        side
        title="Daily steps by domain"
        hint="Every recorded movement, stacked by workflow."
        summary={`Daily steps by domain. Total ${fmtN(kpis.steps)} steps over ${daily.length} days.`}
        legend={anyDaily ? (
          <LivingLegend
            stacked
            density="compact"
            title="Domains"
            mode="toggle"
            items={domainItems}
            selected={shownDomains}
            onChange={setShownDomains}
            scope="Shows or hides a domain's band. Totals and the figures above do not change."
          />
        ) : undefined}
        foot={kpis.undatedSteps > 0 ? (
          <p className="tide-chart__foot" data-testid="undated-caveat">
            {fmtN(kpis.undatedSteps)} imported {kpis.undatedSteps === 1 ? 'step has' : 'steps have'} no recorded date and {kpis.undatedSteps === 1 ? 'is' : 'are'} not plotted.
          </p>
        ) : undefined}
      >
        {!anyDaily ? <EmptyNote>No steps were recorded in this window.</EmptyNote> : (
          <ResponsiveContainer width="100%" height="100%" minHeight={280}>
            <AreaChart data={daily} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid {...gridProps} vertical={false} />
              <XAxis dataKey="date" tickFormatter={fmtDay} tick={axisTick} axisLine={axisLine} tickLine={false} minTickGap={20} />
              <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} width={40} />
              <Tooltip {...tooltipProps} />
              {DOMAINS.map((d) => (
                <Area
                  key={d} type="monotone" dataKey={d} name={DOMAIN_LABEL[d]} stackId="steps"
                  stroke={DOMAIN_COLOR[d]} strokeWidth={1.5} fill={DOMAIN_COLOR[d]} fillOpacity={0.55}
                  hide={!shownDomains.includes(d)} {...motion}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <div className="grid gap-4 lg:grid-cols-3">
        <ChartCard
          title="Who did it"
          hint="Imports are Monday sync and backfill, not people working in eMerge."
          summary={`Origin of steps: ${ORIGINS.map((o) => `${ORIGIN_LABEL[o]} ${fmtN(origin[o] || 0)} (${pct(origin[o] || 0, total)}%)`).join(', ')}.`}
          legend={total === 0 ? undefined : <LivingLegend stacked density="compact" title="Origin" items={originItems} />}
        >
          {total === 0 ? <EmptyNote>No steps in this window.</EmptyNote> : (
            <ResponsiveContainer width="100%" height={56}>
              <BarChart data={originRow} layout="vertical" stackOffset="expand" margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" hide />
                <Tooltip {...tooltipProps} formatter={(v: number, name: string) => [fmtN(v), name]} />
                {ORIGINS.map((o: Origin) => (
                  <Bar key={o} dataKey={o} name={ORIGIN_LABEL[o]} stackId="o" fill={ORIGIN_COLOR[o]} stroke="var(--tide-surface)" strokeWidth={2} {...STATIC} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard
          title="Open work now"
          hint="Current counts, not limited to the window."
          summary={`Open work now: ${openRows.map((r) => `${r.label} ${fmtN(r.value)}`).join(', ')}.`}
          legend={<LivingLegend density="compact" title="Colour is the domain" items={openWorkItems} />}
        >
          <ResponsiveContainer width="100%" height={270}>
            <BarChart data={openRows} layout="vertical" margin={{ top: 4, right: 40, left: 8, bottom: 0 }}>
              <CartesianGrid {...gridProps} horizontal={false} />
              <XAxis type="number" tick={axisTick} axisLine={axisLine} tickLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="label" tick={axisTick} axisLine={false} tickLine={false} width={120} />
              <Tooltip {...tooltipProps} formatter={(v: number) => [fmtN(v), 'Open']} />
              <Bar dataKey="value" name="Open" radius={[0, 4, 4, 0]} {...STATIC}>
                {openRows.map((r) => <Cell key={r.key} fill={r.color} />)}
                <LabelList dataKey="value" position="right" fill={C.foreground} fontSize={12} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Cleanup exits per day"
          hint="PA cases closed, by the status word they closed with."
          summary={`Cleanup exits by status word: ${words.map((wd) => `${wd} ${cleanupDaily.filter((r) => r.word === wd).reduce((s, r) => s + r.count, 0)}`).join(', ') || 'none'}.`}
          legend={words.length === 0 ? undefined : (
            <LivingLegend
              density="compact"
              title="Status word"
              mode="toggle"
              items={wordItems}
              selected={visibleWords}
              onChange={setShownWords}
              scope="Shows or hides a word's bars. The Cleanup exits figure does not change."
            />
          )}
        >
          {cleanupRows.length === 0 ? <EmptyNote>No PA cases were closed in this window.</EmptyNote> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={cleanupRows} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid {...gridProps} vertical={false} />
                <XAxis dataKey="date" tickFormatter={fmtDay} tick={axisTick} axisLine={axisLine} tickLine={false} minTickGap={20} />
                <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} width={40} />
                <Tooltip {...tooltipProps} />
                {words.map((wd) => (
                  <Bar key={wd} dataKey={wd} stackId="w" fill={wordToken(wd).color} stroke="var(--tide-surface)" strokeWidth={1} hide={!visibleWords.includes(wd)} {...motion} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    </div>
  );
}
